import { useState, useEffect } from "react";
import { useTranslations, useLanguage } from "contexts/LocalizationContext";
import { getPageUrl } from "utils/getPageUrl";
import {
  Input,
  Select,
  SelectCountries,
  Terms,
  FormSubmitButton,
  FormSuccessMessage,
  FormError,
} from "components/FormControl";
import { useForm } from "react-hook-form";
import axios from "axios";
import { useRouter } from "next/router";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import { ChakraFlex as Flex, ChakraVStack as VStack } from "@airhelp/react-ui-components";
import {
  typeOfReferrerOptions,
  mainPromotionRouteOptions,
  monthlyTrafficEstimationOptions,
  mainPromotionalLanguageOptions,
  FORM_SUBMIT_EVENT_EVENT_UA,
  FORM_SUBMIT_EVENT_EVENT,
} from "./config";
import type { AffiliateFormDataProps } from "./AffiliateForm.d";

import Header from "./Header";
import OptionalInputLabel from "./OptionalInputLabel";

export const AffiliateForm = () => {
  const { query } = useRouter();
  const translations = useTranslations();
  const language = useLanguage();

  const {
    handleSubmit,
    register,
    setError,
    formState: { isSubmitSuccessful, isSubmitting, isSubmitted, errors },
    watch,
  } = useForm<AffiliateFormDataProps>();
  const watchTermsAccepted = watch("agreeWithTerms", false);
  const [formTrackingParams, setFormTrackingParams10] = useState("");

  useEffect(() => {
    if (!query.utm_content || !query.utm_medium || !query.utm_source) return;

    setFormTrackingParams10(`${query.utm_content}/${query.utm_medium} & ${query.utm_source}`);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const termsTranslationVariables = {
    "%terms-link-attrs%": `href="${getPageUrl(
      language,
      "affiliateTerms"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "terms_link_title"
    )}" style="text-decoration: underline"`,
    "%privacy-link-attrs%": `href="${getPageUrl(
      language,
      "affiliatePrivacy"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "terms_privacy_link_title"
    )}" style="text-decoration: underline"`,
  };

  const handleOnSubmit = async (data: AffiliateFormDataProps) => {
    const requestData = { ...data, data10: formTrackingParams };
    try {
      await axios.post("/api/affiliate/", requestData);
      trackUAEvent(FORM_SUBMIT_EVENT_EVENT_UA);
      trackCustomEvent(FORM_SUBMIT_EVENT_EVENT);
    } catch (error) {
      setError("serverError", {
        type: "Server Side",
        message: translations("subscribe_form_submit_error"),
      });
      throw Error(error);
    }
  };

  return (
    <Flex flexDir="column" p={{ base: 4, lg: 10 }}>
      <Header text={translations("form_affiliate_heading")} />
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <VStack spacing={6}>
          <Input
            name="firstname"
            errors={errors}
            label={translations("affiliate_first_name")}
            disabled={isSubmitSuccessful}
            register={register({ required: true, maxLength: 50 })}
          />
          <Input
            name="lastname"
            errors={errors}
            label={translations("affiliate_last_name")}
            disabled={isSubmitSuccessful}
            register={register({ required: true, maxLength: 50 })}
          />
          <Input
            name="data2"
            errors={errors}
            label={
              <OptionalInputLabel label={translations("affiliate_company")} optionalText={translations("optional")} />
            }
            disabled={isSubmitSuccessful}
            register={register({ maxLength: 50 })}
          />
          <Input
            name="username"
            type="email"
            errors={errors}
            label={translations("email_address")}
            disabled={isSubmitSuccessful}
            register={register({ required: true, maxLength: 50 })}
          />
          <SelectCountries
            name="data6"
            label={translations("country")}
            placeholder={translations("affiliate_dropdown_list_select_option")}
            errors={errors}
            disabled={isSubmitSuccessful}
            register={register({ required: true })}
          />
          <Input
            name="refid"
            errors={errors}
            label={
              <OptionalInputLabel
                label={translations("affiliate_referral_id")}
                optionalText={translations("optional")}
              />
            }
            helperText={translations("affiliate_referral_id_description")}
            disabled={isSubmitSuccessful}
            register={register({ maxLength: 50 })}
          />
          <Select
            name="data9"
            label={translations("affiliate_dropdown_field_name_type_of_referrer")}
            placeholder={translations("affiliate_dropdown_list_select_option")}
            options={typeOfReferrerOptions}
            errors={errors}
            disabled={isSubmitSuccessful}
            register={register({ required: true })}
          />
          <Select
            name="data16"
            label={translations("affiliate_dropdown_main_promotion_route")}
            placeholder={translations("affiliate_dropdown_list_select_option")}
            options={mainPromotionRouteOptions}
            errors={errors}
            disabled={isSubmitSuccessful}
            register={register({ required: true })}
          />
          <Input
            name="data1"
            errors={errors}
            type="url"
            label={
              <OptionalInputLabel label={translations("affiliate_web_url")} optionalText={translations("optional")} />
            }
            helperText={translations("affiliate_web_url_description")}
            disabled={isSubmitSuccessful}
            register={register({ maxLength: 50 })}
          />
          <Select
            name="data18"
            placeholder={translations("affiliate_dropdown_list_select_option")}
            label={
              <OptionalInputLabel
                label={translations("affiliate_dropdown_monthly_traffic_estimation")}
                optionalText={translations("optional")}
              />
            }
            options={monthlyTrafficEstimationOptions}
            errors={errors}
            disabled={isSubmitSuccessful}
            register={register()}
          />
          <Select
            name="data21"
            placeholder={translations("affiliate_dropdown_list_select_option")}
            label={
              <OptionalInputLabel
                label={translations("affiliate_dropdown_main_promotional_language")}
                optionalText={translations("optional")}
              />
            }
            options={mainPromotionalLanguageOptions}
            errors={errors}
            disabled={isSubmitSuccessful}
            register={register()}
          />
          <Terms
            register={register({ required: true })}
            isDisabled={isSubmitSuccessful}
            htmlContent={translations("agree_to_terms", termsTranslationVariables)}
          />
          {isSubmitted && isSubmitSuccessful && <FormSuccessMessage text={translations("form_submit_success")} />}
          {isSubmitted && errors.serverError && (
            <FormError formError={!!errors.serverError} formErrorText={errors.serverError.message} id="form-error" />
          )}
          <FormSubmitButton
            text={translations("form_affiliate_submit_button")}
            isDisabled={!watchTermsAccepted || isSubmitSuccessful}
            isLoading={isSubmitting}
            mt={4}
          />
        </VStack>
      </form>
    </Flex>
  );
};
